import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Contractor } from '../../objects/Contractor';
import { ContractorService } from '../../Services/contractor.service';
import { AuthService } from '../../components/services/auth.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-contractor-grid',
  templateUrl: './contractor-grid.component.html',
  styleUrls: ['./contractor-grid.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractorGridComponent implements OnInit {
  contractors: Contractor[] = [];
  filtered: Contractor[] = [];
  searchTerm = '';
  tradeFilter = '';
  isLoading = true;
  loadError = false;
  isAdmin = false;

  constructor(
    private contractorService: ContractorService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    this.isAdmin = user?.role === 'admin';
    this.loadContractors();
  }

  loadContractors(): void {
    this.isLoading = true;
    this.loadError = false;

    const user = this.authService.getCurrentUser();
    const obs$ = this.isAdmin
      ? this.contractorService.getContractors()
      : this.contractorService.getContractorsByProvider(user!.id);

    obs$.subscribe({
      next: (contractors) => {
        this.contractors = contractors || [];
        this.applyFilter();
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadError = true;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  get uniqueTrades(): string[] {
    const trades = this.contractors
      .map(c => (c.jobTitle || '').trim())
      .filter(t => t.length > 0);
    return [...new Set(trades)].sort();
  }

  onSearch(): void {
    this.applyFilter();
    this.cdr.markForCheck();
  }

  setTradeFilter(trade: string): void {
    this.tradeFilter = this.tradeFilter === trade ? '' : trade;
    this.applyFilter();
    this.cdr.markForCheck();
  }

  applyFilter(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.filtered = this.contractors.filter(c => {
      const matchesSearch = !term ||
        c.name?.toLowerCase().includes(term) ||
        c.jobTitle?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.cellPhone?.includes(term);
      const matchesTrade = !this.tradeFilter ||
        (c.jobTitle || '').trim() === this.tradeFilter;
      return matchesSearch && matchesTrade;
    });
  }

  trackById(_index: number, c: Contractor): number { return c.id; }

  editContractor(contractor: Contractor): void {
    this.router.navigate(['/contractors/edit', contractor.id]);
  }

  deleteContractor(contractor: Contractor): void {
    if (!confirm(`Remove ${contractor.name}? This cannot be undone.`)) { return; }
    this.contractorService.deleteContractor(contractor.id).subscribe({
      next: () => {
        this.contractors = this.contractors.filter(c => c.id !== contractor.id);
        this.applyFilter();
        this.cdr.markForCheck();
      },
      error: () => {
        alert('Failed to delete contractor. Please try again.');
      }
    });
  }

  addContractor(): void {
    this.router.navigate(['/contractors/add']);
  }

  imageUrl(id: number): string {
    return this.contractorService.getContractorImageUrl(id);
  }

  avatarFallback(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    const parent = img.parentElement;
    if (parent) { parent.classList.add('show-initials'); }
  }

  initials(name: string): string {
    if (!name) { return '?'; }
    const parts = name.trim().split(' ');
    return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '');
  }
}
